import { type NowContext, nowContext } from "@hq/core";
import { useSyncExternalStore } from "react";

type Ticker = {
  subscribe(onChange: () => void): () => void;
  snapshot(): NowContext;
};

const tickers = new Map<number, Ticker>();

/** One interval per tick length shared by every subscriber, started on first use and stopped on last. */
function tickerFor(tickMs: number): Ticker {
  let ticker = tickers.get(tickMs);
  if (ticker) return ticker;
  const listeners = new Set<() => void>();
  let current = nowContext();
  let handle: ReturnType<typeof setInterval> | undefined;
  ticker = {
    subscribe(onChange) {
      if (listeners.size === 0) {
        current = nowContext();
        handle = setInterval(() => {
          current = nowContext();
          for (const l of [...listeners]) l();
        }, tickMs);
      }
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0 && handle !== undefined) {
          clearInterval(handle);
          handle = undefined;
        }
      };
    },
    snapshot: () => current,
  };
  tickers.set(tickMs, ticker);
  return ticker;
}

/**
 * The current instant and zone, refreshed every `tickMs` so "today" rolls over at midnight
 * without a reload. Pass it to `@hq/core` view functions.
 */
export function useNowContext(tickMs = 60_000): NowContext {
  const ticker = tickerFor(tickMs);
  return useSyncExternalStore(ticker.subscribe, ticker.snapshot, ticker.snapshot);
}
